/**
 * TabBar — the 2026 section-navigation strip, shared by the patient profile
 * and the toolbox.
 *
 * Desktop (>768px): flat, flush, full-width tab buttons (design-2026 §3).
 * Mobile (≤768px): a detached "page picker" — a mint trigger card that opens a
 * bottom sheet listing every tab (icon tile + label + badge + check). Seven
 * tabs never fit a phone row, and a native <select> can't style its options,
 * so users didn't recognise it as navigation.
 *
 * Both renderings are always in the DOM; CSS decides which one shows. The
 * desktop buttons therefore keep working for programmatic `.click()` (the
 * onboarding tour finds tabs by text) even on mobile.
 *
 * Props:
 *   tabs         [{ id, label, icon?: PhosphorIcon, badge?: number|string,
 *                   link?: boolean, onSelect?: () => void }]
 *                `link` renders an outbound entry (dashed divider, never "active");
 *                `onSelect` runs instead of onChange(id) when given.
 *   activeId     id of the active tab
 *   onChange     (id) => void
 *   className    extra classes for the wrapper (keep legacy anchors here)
 *   tabClassName extra classes for each desktop button (legacy anchors)
 *   ariaLabel    accessible name for the picker
 *   sheetTitle   heading of the mobile sheet (default "לאן לעבור?")
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CaretDown, Check, ArrowSquareOut } from "@phosphor-icons/react";
import ModalCloseButton from "./ModalCloseButton";
import "./TabBar.css";

const TabBar = ({
  tabs,
  activeId,
  onChange,
  className = "",
  tabClassName = "",
  ariaLabel = "ניווט",
  sheetTitle = "לאן לעבור?",
}) => {
  const [open, setOpen] = useState(false);
  const active = tabs.find((t) => t.id === activeId) || tabs[0];
  const ActiveIcon = active?.icon;

  const pick = (id) => {
    const tab = tabs.find((t) => t.id === id);
    setOpen(false);
    if (!tab) return;
    if (tab.onSelect) tab.onSelect();
    else onChange?.(id);
  };

  // Escape closes the sheet
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className={`ck-tabs ${className}`.trim()}>
      <div className="ck-tabs-strip" role="tablist">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = !tab.link && tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              role={tab.link ? undefined : "tab"}
              aria-selected={tab.link ? undefined : isActive}
              className={`ck-tab ${tabClassName} ${isActive ? "active" : ""} ${
                tab.link ? "ck-tab--link" : ""
              }`.replace(/\s+/g, " ").trim()}
              onClick={() => pick(tab.id)}
            >
              {Icon && <Icon size={16} weight={isActive ? "fill" : "regular"} />}
              <span>{tab.label}</span>
              {tab.badge ? <span className="ck-tab-badge">{tab.badge}</span> : null}
            </button>
          );
        })}
      </div>

      {/* ===== Mobile page picker (hidden on desktop) ===== */}
      <div className="ck-tabs-picker">
        <button
          type="button"
          className="ck-tabs-trigger"
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <span className="ck-tabs-trigger-icon">
            {ActiveIcon && <ActiveIcon size={18} weight="fill" />}
          </span>
          <span className="ck-tabs-trigger-text">
            <span className="ck-tabs-trigger-eyebrow">מעבר בין עמודים</span>
            <span className="ck-tabs-trigger-label">{active?.label}</span>
          </span>
          <span className="ck-tabs-trigger-caret">
            <CaretDown size={16} weight="bold" />
          </span>
        </button>

        {/* Portal: the strip sits in a z-indexed stacking context inside the
            host card, so the sheet must escape to <body> to cover everything */}
        {open && createPortal(
          <div
            className="ck-tabs-overlay"
            onClick={() => setOpen(false)}
            role="presentation"
          >
            <div
              className="ck-tabs-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={sheetTitle}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ck-tabs-sheet-head">
                <span className="ck-tabs-sheet-title">{sheetTitle}</span>
                <ModalCloseButton onClick={() => setOpen(false)} />
              </div>
              <div className="ck-tabs-sheet-list" role="listbox" aria-label={ariaLabel}>
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = !tab.link && tab.id === activeId;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`ck-tabs-option${isActive ? " active" : ""}${
                        tab.link ? " ck-tabs-option--link" : ""
                      }`}
                      onClick={() => pick(tab.id)}
                    >
                      <span className="ck-tabs-option-icon">
                        {Icon && <Icon size={18} weight={isActive ? "fill" : "regular"} />}
                      </span>
                      <span className="ck-tabs-option-label">{tab.label}</span>
                      {tab.badge ? (
                        <span className="ck-tab-badge">{tab.badge}</span>
                      ) : null}
                      {isActive && <Check size={18} weight="bold" />}
                      {tab.link && <ArrowSquareOut size={16} weight="bold" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default TabBar;
